import { useT, Namespace } from "@racoon/i18n";
import { useEffect, useState } from "react";
import { useContractOperations } from "../hooks/useContractOperations";
import {
  useCreateServiceReceiverMutation,
  useUpdateServiceReceiverMutation,
} from "~/shared/api/organization/organization.grpc";
import { Organization, ConnectProto } from "@racoon/api";
import {
  ModalFooter,
  Button,
  CardTabs,
  FieldRow,
  shouldShowCardFooter,
  useModal,
} from "@racoon/ui";
import { CustomerInfoTab } from "./CustomerInfoTab";
import { LabelSelect } from "~/shared/ui/label.select";
import { ContractsTab } from "./contracts/ContractsTab";
import { ObjectsTab } from "./ObjectsTab";
import { toAmount } from "~/shared/lib/tariff-rate";
import { AddContractForm, type ContractFormData } from "./contracts/contract-modal.content";
import { useContractEditModal } from "../hooks/use-contract-edit-modal";
import {
  useCreateContractMutation,
  useUploadContractFilesMutation,
} from "~/shared/api/contract/contract.grpc";
import { CustomerContactsBlock } from "./ContactsTab";
import { useCreateContactMutation } from "~/shared/api/contact/contact.grpc";
import {
  isBlank,
  isRequiredBinValid,
  isRequiredTextValid,
  toTimestamp,
  validateBin,
  validateRequiredText,
} from "@racoon/lib";
import { browserFileToProtoFile } from "../lib/fileUploadUtils";
import { useCustomerBinDuplicate } from "~/shared/hooks/use-bin-duplicate";
import {
  useCustomerTypeOptions,
  useSetCustomerType,
  useStoredCustomerTypeId,
} from "~/shared/hooks/use-customer-types";

type CustomerCardTab = "info" | "contracts" | "objects" | "contacts";

/**
 * The org model stores one `director_full_name`, while the card edits surname and
 * given name separately. Kazakh convention puts the surname first, so the first
 * token is the surname and the remainder is the given name(s).
 */
function splitDirectorName(full: string): { lastName: string; firstName: string } {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { lastName: "", firstName: "" };
  return { lastName: parts[0], firstName: parts.slice(1).join(" ") };
}

function joinDirectorName(lastName: string, firstName: string): string {
  return [lastName.trim(), firstName.trim()].filter(Boolean).join(" ");
}

/** Round-trips a stored name through split+join so it can be compared like-for-like. */
function normalizeDirectorName(full: string): string {
  const { lastName, firstName } = splitDirectorName(full);
  return joinDirectorName(lastName, firstName);
}

export interface CustomerCardContentProps {
  initial?: Partial<ConnectProto.Organization>;
  onSaved?: (org: Organization) => void;
  providerId?: number | null;
}

// Type for pending contracts using ConnectProto.ContractCreate
type PendingContractCreate = Omit<
  ConnectProto.ContractCreate,
  "serviceProviderId" | "targetOrganizationId"
> & {
  serviceProviderId?: bigint | number;
  targetOrganizationId?: bigint;
  localFiles?: File[];
};

export const CustomerCardContent = ({ initial, onSaved, providerId }: CustomerCardContentProps) => {
  const t = useT();
  const { openModal, closeModal } = useModal();
  const [tab, setTab] = useState<CustomerCardTab>("info");
  const [name, setName] = useState("");
  const [bin, setBin] = useState("");
  const [jur_address, setJurAddress] = useState("");
  const [fact_address, setFactAddress] = useState("");
  const [directorLastName, setDirectorLastName] = useState("");
  const [directorFirstName, setDirectorFirstName] = useState("");
  const [primaryContactId, setPrimaryContactId] = useState<number | undefined>(undefined);
  // The provider's customer type: stored on the provider–customer pair, edited here,
  // written by «Сохранить» (after creation for a new customer). undefined: untouched.
  const [customerTypeId, setCustomerTypeId] = useState<string>();
  const saveCustomerType = useSetCustomerType();

  // Local state for contracts to be created before client exists
  const [pendingContracts, setPendingContracts] = useState<PendingContractCreate[]>([]);

  // Local state for contacts to be created before client exists
  // gRPC shape: this state is handed straight to ContactsTab and, on save,
  // to createContact.
  const [pendingContacts, setPendingContacts] = useState<ConnectProto.Contact[]>([]);

  // initial.id is a proto-es bigint; the customer-card children are keyed by number.
  const receiverId = initial?.id != null ? Number(initial.id) : undefined;
  const storedCustomerTypeId = useStoredCustomerTypeId(providerId, receiverId);
  const shownCustomerTypeId = customerTypeId ?? storedCustomerTypeId ?? "0";
  const customerTypeOptions = useCustomerTypeOptions(providerId, shownCustomerTypeId);
  const customerTypeChanged =
    customerTypeId !== undefined && customerTypeId !== (storedCustomerTypeId ?? "0");

  const {
    contracts,
    contractsLoading,
    contractChanges,
    saveChanges,
    refreshContracts,
    toggleArchive,
  } = useContractOperations(receiverId);
  const [archiving, setArchiving] = useState(false);
  const { openEdit: openEditContract } = useContractEditModal();

  const [create, { isLoading: creating }] = useCreateServiceReceiverMutation();
  const [update, { isLoading: updating }] = useUpdateServiceReceiverMutation();
  const [createContractGrpc] = useCreateContractMutation();
  const [uploadContractFiles] = useUploadContractFilesMutation();
  const [createContact] = useCreateContactMutation();

  // No local state for modal dialogs needed; we use ModalProvider

  useEffect(() => {
    if (initial) {
      setName(initial.name ?? "");
      setBin(initial.bin ?? "");
      setJurAddress(initial.jurAddress ?? "");
      setFactAddress(initial.factAddress ?? "");
      const director = splitDirectorName(initial.directorFullName ?? "");
      setDirectorLastName(director.lastName);
      setDirectorFirstName(director.firstName);
      setPrimaryContactId(
        initial.primaryContactId != null ? Number(initial.primaryContactId) : undefined,
      );
    }
    setCustomerTypeId(undefined);
  }, [initial]);

  // Add contract to local state if client is not yet created
  const handleAddContract = async (data: ContractFormData) => {
    const startAt = data.startDate ? toTimestamp(data.startDate) : toTimestamp(new Date());
    const endAt = data.endDate
      ? toTimestamp(data.endDate)
      : toTimestamp(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000));

    if (!initial?.id) {
      setPendingContracts((prev) => [
        ...prev,
        {
          dealNumber: data.dealNumber,
          name: data.name,
          type: data.type,
          serviceProviderId: BigInt(data.serviceProviderId || providerId || 0),
          startAt,
          endAt,
          tariffRateByJobType: data.tariffRateByJobType,
          rentCost: toAmount(data.rentCost),
          monthlyFee: toAmount(data.monthlyFee),
          localFiles: data.files,
        } as PendingContractCreate,
      ]);
      return;
    }

    const contract = await createContractGrpc({
      dealNumber: data.dealNumber,
      name: data.name,
      type: data.type,
      serviceProviderId: BigInt(data.serviceProviderId || providerId || 0),
      targetOrganizationId: BigInt(initial.id),
      startAt,
      endAt,
      tariffRateByJobType: data.tariffRateByJobType,
      rentCost: toAmount(data.rentCost),
      monthlyFee: toAmount(data.monthlyFee),
    }).unwrap();

    if (data.files && data.files.length > 0) {
      const protoFiles = await Promise.all(data.files.map(browserFileToProtoFile));
      await uploadContractFiles({ contractId: contract.id, files: protoFiles }).unwrap();
    }

    refreshContracts();
  };

  const handleAddContractClick = () => {
    const modalId = openModal({
      closeButtonTop: "top-4",
      size: "xl",
      title: t(Namespace.Customers, "modal.addContract.title"),
      bodyClassName: "!p-0",
      body: (
        <AddContractForm
          onSubmit={async (data) => {
            await handleAddContract(data);
            closeModal(modalId);
          }}
          onCancel={() => {
            closeModal(modalId);
          }}
        />
      ),
    });
  };

  const handleEditContractClick = (contract: ConnectProto.Contract) => {
    if (typeof contract.id === "string" && (contract.id as string).startsWith("pending-")) {
      return;
    }
    openEditContract(contract, { onDone: refreshContracts });
  };

  const nameIssue = validateRequiredText(name);
  const binIssue = validateBin(bin);
  // Looked up only once the BIN is changed: a customer already sharing its BIN with another
  // must still be editable, or that existing clash would lock every other field too.
  const {
    duplicate: binDuplicate,
    archived: binDuplicateArchived,
    pending: binCheckPending,
    failed: binCheckFailed,
  } = useCustomerBinDuplicate({
    providerId,
    bin,
    excludeOrgId: initial?.id,
    skip: bin.trim() === (initial?.bin ?? "").trim(),
  });
  const isValid =
    isRequiredTextValid(name) &&
    isRequiredBinValid(bin) &&
    !binDuplicate &&
    !binCheckPending &&
    !binCheckFailed;
  // One message under the BIN, the most pressing first. A new card leaves an empty BIN
  // unflagged, like the name; a stored customer saved without one says why Save is disabled.
  const binError = binIssue
    ? t(Namespace.Validation, binIssue.key, binIssue.vars)
    : binDuplicate
      ? t(Namespace.Customers, binDuplicateArchived ? "card.binTakenArchived" : "card.binTaken", {
          name: binDuplicate.name?.trim() || t(Namespace.Common, "unnamed"),
        })
      : binCheckFailed
        ? t(Namespace.Customers, "card.binCheckFailed")
        : initial?.id != null && isBlank(bin)
          ? t(Namespace.Validation, "required")
          : undefined;

  // Save handler: create client, then create contracts if any pending
  const handleSave = async () => {
    if (!isValid) return;
    let saved: Organization | undefined;
    if (initial?.id) {
      // Send the raw values, including empty strings. These fields have explicit
      // presence in the proto (`optional string`), so "" is transmitted as
      // present-and-empty and clears the stored value, whereas `undefined` is
      // omitted from the wire and leaves the old value in place — which made it
      // impossible to clear a field by emptying it.
      saved = (await update({
        id: BigInt(initial.id!),
        name: name.trim(),
        bin: bin.trim(),
        jurAddress: jur_address,
        factAddress: fact_address,
        directorFullName: joinDirectorName(directorLastName, directorFirstName),
      }).unwrap()) as unknown as Organization;
    } else {
      if (!providerId) return;
      saved = (await create({
        serviceProviderId: BigInt(providerId),
        organization: {
          name: name.trim(),
          type: ConnectProto.OrganizationType.SERVICE_RECEIVER,
          bin: bin.trim() || undefined,
          jurAddress: jur_address || undefined,
          factAddress: fact_address || undefined,
          directorFullName: joinDirectorName(directorLastName, directorFirstName) || undefined,
        },
      }).unwrap()) as unknown as Organization;

      // After client is created, create all pending contracts
      if (pendingContracts.length > 0 && saved) {
        const savedId = saved.id;
        await Promise.all(
          pendingContracts.map(async (contract) => {
            const created = await createContractGrpc({
              dealNumber: contract.dealNumber ?? "",
              name: contract.name ?? "",
              type: contract.type ?? ConnectProto.ContractType.WASTE_OPERATOR,
              serviceProviderId: BigInt(contract.serviceProviderId ?? providerId ?? 0),
              targetOrganizationId: BigInt(savedId),
              startAt: contract.startAt ?? toTimestamp(new Date()),
              endAt:
                contract.endAt ?? toTimestamp(new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)),
              tariffRateByJobType: contract.tariffRateByJobType,
              rentCost: contract.rentCost,
              monthlyFee: contract.monthlyFee,
            }).unwrap();

            if (contract.localFiles && contract.localFiles.length > 0) {
              const protoFiles = await Promise.all(contract.localFiles.map(browserFileToProtoFile));
              await uploadContractFiles({ contractId: created.id, files: protoFiles }).unwrap();
            }
          }),
        );
        setPendingContracts([]);
      }

      // After client is created, create all pending CONTACTS
      // contact.id is an int64, and connectBaseQuery normalizes every bigint in
      // a response to a decimal string — so it arrives as a string whatever the
      // generated type says. Parsed straight to a bigint below: going through
      // Number first would round anything past 2^53 and make a different
      // contact primary.
      let createdPrimaryContactId: bigint | undefined;

      if (pendingContacts.length > 0 && saved) {
        const savedId = saved.id;
        try {
          const createdContacts = await Promise.all(
            pendingContacts.map((contact) =>
              createContact({
                name: contact.name,
                phone: contact.phone,
                email: contact.email,
                position: contact.position,
                ownerOrganizationId: BigInt(savedId), // Link to the new organization
              }).unwrap(),
            ),
          );

          // If a primary contact was selected from pending contacts, find its new real ID
          if (primaryContactId && primaryContactId < 0) {
            // Pending contacts carry bigint ids from the proto shape, while the
            // selected primary id is a plain number — compare on value.
            const pendingIndex = pendingContacts.findIndex(
              (c) => Number(c.id) === primaryContactId,
            );
            if (pendingIndex !== -1 && createdContacts[pendingIndex]) {
              createdPrimaryContactId = BigInt(createdContacts[pendingIndex].id);
            }
          }
        } catch (e) {
          console.error("Failed to create pending contacts", e);
        }
        setPendingContacts([]);
      }

      // If we identified a primary contact from the newly created ones, update the organization
      if (createdPrimaryContactId && saved) {
        try {
          const updatedOrg = await update({
            id: BigInt(saved.id!),
            primaryContactId: createdPrimaryContactId,
          }).unwrap();
          saved = updatedOrg as unknown as Organization;
        } catch (e) {
          console.error("Failed to set primary contact", e);
        }
      }
    }

    if (saved && providerId && customerTypeId !== undefined && customerTypeChanged) {
      try {
        await saveCustomerType(providerId, BigInt(saved.id), customerTypeId);
      } catch {
        // GlobalErrorToaster shows the API error; the customer itself is saved.
      }
    }

    // Save all contract changes (edits to existing contracts)
    await Promise.all(Object.entries(contractChanges).map(([id]) => saveChanges(parseInt(id))));

    if (saved) {
      onSaved?.(saved);
      closeModal();
    }
  };

  // The customers list derives a customer's status from its contracts, so archiving
  // archives this provider's contracts with the customer (restore reactivates them).
  const isArchived = !contracts?.some((c) => c.status === ConnectProto.ContractStatus.ACTIVE);

  const handleToggleArchive = async () => {
    if (!initial?.id || !contracts?.length) return;
    const from = isArchived
      ? ConnectProto.ContractStatus.INACTIVE
      : ConnectProto.ContractStatus.ACTIVE;
    setArchiving(true);
    try {
      await Promise.all(
        contracts
          .filter((c) => c.status === from)
          .map((c) =>
            toggleArchive(Number(c.id), isArchived, c.serviceProviderId, c.targetOrganizationId),
          ),
      );
      onSaved?.(initial as unknown as Organization);
      closeModal();
    } catch (e) {
      console.error("Failed to toggle customer status", e);
    } finally {
      setArchiving(false);
    }
  };

  // Combine contracts from API and pending (for display)
  const allContracts = initial?.id
    ? contracts
    : [
        ...(contracts || []),
        ...pendingContracts.map(
          (c, idx) =>
            ({
              ...c,
              id: `pending-${idx + 1}` as any,
              targetOrganizationId: BigInt(0),
            }) as any,
        ),
      ];

  const isEdit = initial?.id != null;

  /** Single source of truth for this card's sections — the sidebar derives from it. */
  const allTabItems: { key: CustomerCardTab; label: string }[] = [
    { key: "info", label: t(Namespace.Customers, "card.tab.info") },
    { key: "contracts", label: t(Namespace.Customers, "card.tab.contracts") },
    { key: "contacts", label: t(Namespace.PickupLocation, "contacts") },
    { key: "objects", label: t(Namespace.Customers, "card.tab.objects") },
  ];
  // Objects lists container groups by org id — nothing can exist before the
  // customer is saved, and unlike contracts/contacts there is no staging path
  // for them (they are born through pickup-location flows). Every other tab
  // works pre-save: it stages locally and handleSave flushes after create.
  const tabItems = isEdit ? allTabItems : allTabItems.filter((item) => item.key !== "objects");

  // This card holds its fields in plain state rather than react-hook-form, so
  // "dirty" is derived by comparing against the record it was opened with.
  // The director name is compared after normalisation on BOTH sides: the two inputs
  // are derived by splitting the stored value, and re-joining them collapses
  // whitespace. Comparing the re-joined value against the raw stored one would
  // report a stored " Ivanov  Ivan " as dirty the moment the card opened, which
  // would force the footer onto every tab.
  const isDirty =
    name !== (initial?.name ?? "") ||
    bin !== (initial?.bin ?? "") ||
    jur_address !== (initial?.jurAddress ?? "") ||
    fact_address !== (initial?.factAddress ?? "") ||
    joinDirectorName(directorLastName, directorFirstName) !==
      normalizeDirectorName(initial?.directorFullName ?? "") ||
    customerTypeChanged;

  // Save also commits staged contract edits, so it must be reachable from
  // `contracts` as well as `info`.
  const showFooter = shouldShowCardFooter<CustomerCardTab>({
    isEdit,
    activeTab: tab,
    footerTabs: ["info", "contracts"],
    isDirty,
  });
  const showStatusActions =
    isEdit &&
    tab === "info" &&
    !!contracts?.some((c) => c.status !== ConnectProto.ContractStatus.HIDDEN);

  const panels = (
    <>
      {tab === "info" ? (
        <CustomerInfoTab
          name={name}
          bin={bin}
          nameError={nameIssue && t(Namespace.Validation, nameIssue.key, nameIssue.vars)}
          binError={binError}
          jur_address={jur_address}
          fact_address={fact_address}
          onNameChange={setName}
          onBinChange={setBin}
          onJurAddressChange={setJurAddress}
          onFactAddressChange={setFactAddress}
          directorLastName={directorLastName}
          directorFirstName={directorFirstName}
          onDirectorLastNameChange={setDirectorLastName}
          onDirectorFirstNameChange={setDirectorFirstName}
          customerTypeField={
            // A provider with no customer types gets no field.
            customerTypeOptions.length > 0 && (
              <FieldRow
                label={t(Namespace.Customers, "card.customerType.label")}
                htmlFor="customer-type"
              >
                <LabelSelect
                  id="customer-type"
                  options={customerTypeOptions}
                  value={shownCustomerTypeId}
                  onChange={setCustomerTypeId}
                  // An existing customer's type is shown once it is known.
                  disabled={isEdit && storedCustomerTypeId === undefined}
                />
              </FieldRow>
            )
          }
        />
      ) : tab === "contracts" ? (
        <ContractsTab
          contracts={allContracts}
          loading={contractsLoading}
          onAddContract={handleAddContractClick}
          onEditContract={handleEditContractClick}
        />
      ) : tab === "objects" ? (
        <ObjectsTab receiverOrgId={receiverId} />
      ) : (
        <CustomerContactsBlock
          organizationId={receiverId}
          primaryContactId={primaryContactId}
          pendingContacts={pendingContacts}
          setPendingContacts={setPendingContacts}
          onPrimaryChange={async (id) => {
            const previousPrimaryContactId = primaryContactId;
            setPrimaryContactId(id);
            if (initial?.id && id > 0) {
              try {
                await update({
                  id: BigInt(initial.id!),
                  primaryContactId: BigInt(id),
                }).unwrap();
              } catch {
                setPrimaryContactId(previousPrimaryContactId);
              }
            }
          }}
        />
      )}
    </>
  );

  return (
    <>
      <CardTabs
        items={tabItems}
        value={tab}
        onValueChange={setTab}
        idPrefix="customer-card"
        panelClassName="space-y-4 px-5 py-4"
      >
        {panels}
      </CardTabs>

      {showFooter && (
        <ModalFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2 border-t px-5 py-4 mt-auto">
          {showStatusActions && (
            <Button
              id="customer-card-archive-button"
              variant={isArchived ? "solidGreen" : "solidRed"}
              className="w-full sm:w-auto"
              onClick={handleToggleArchive}
              disabled={archiving || creating || updating}
            >
              {t(Namespace.Customers, isArchived ? "card.unarchive" : "card.archive")}
            </Button>
          )}
          <Button
            id="customer-card-save-button"
            variant="solidBlue"
            onClick={handleSave}
            disabled={!isValid || creating || updating || archiving}
            className="w-full sm:w-auto"
          >
            {t(Namespace.Customers, "card.save")}
          </Button>
        </ModalFooter>
      )}
    </>
  );
};
